import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  LayoutDashboard, Truck, History, DollarSign, ListFilter, Plus, ArrowUp, 
  Users, Car, Bell, Settings, MapPin, Calendar, Clock, Package, Eye,
  Filter, Search, MoreVertical, Edit, Trash2, UserCheck, UserX,
  AlertTriangle, CheckCircle, XCircle, Phone, CreditCard, Save,
  Building, Mail, Key, Shield, Globe, MapPin as LocationIcon
} from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch-simple';
import { toast } from '@/components/ui/use-toast';
import { useTransporterStats } from '@/hooks/useTransporterStats';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import LogiConnectIdBadge from '@/components/ui/logiconnect-id-badge';
import useAuthStore from '@/store/authStore';
import { getTransporterSidebarLinks } from '@/config/transporterSidebarLinks';

const TransporterSettings = () => {
  const { stats } = useTransporterStats();
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState('profile');
  
  // Données du profil (simulées - à remplacer par les vraies données)
  const [profileData, setProfileData] = useState({
    organisation_name: 'Transport Diallo SARL',
    is_company: true,
    registration_number: 'RC-123456',
    tax_info: 'NIF-789012345',
    transport_domains: ['HEAVY_GOODS', 'LIGHT_GOODS'],
    verification_status: 'VERIFIED',
  });

  const [userData, setUserData] = useState({
    first_name: 'Mamadou',
    last_name: 'Diallo',
    email: 'mamadou.diallo@transport.com',
    phone_number: '+224 123 45 67 89',
  });

  const [preferences, setPreferences] = useState({
    notifications_email: true,
    notifications_sms: false,
    notifications_push: true,
    auto_accept_requests: false,
    show_location: true,
    language: 'fr',
    timezone: 'Africa/Conakry',
  });

  const [securityData, setSecurityData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
    two_factor_enabled: false,
  });
  
  const sidebarLinks = getTransporterSidebarLinks(stats);

  const getVerificationBadge = (status) => {
    const statusConfig = {
      'VERIFIED': { label: 'Vérifié', color: 'bg-green-500' },
      'PENDING': { label: 'En attente', color: 'bg-yellow-500' },
      'UNVERIFIED': { label: 'Non vérifié', color: 'bg-gray-500' },
      'REJECTED': { label: 'Rejeté', color: 'bg-red-500' },
    };
    
    const config = statusConfig[status] || { label: status, color: 'bg-gray-500' };
    return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
  };

  const handleSaveProfile = () => {
    toast({
      title: 'Profil mis à jour',
      description: 'Vos informations de profil ont été sauvegardées.',
    });
  };

  const handleSavePreferences = () => {
    toast({
      title: 'Préférences sauvegardées',
      description: 'Vos préférences ont été mises à jour.',
    });
  };

  const handleChangePassword = () => {
    if (securityData.new_password !== securityData.confirm_password) {
      toast({
        title: 'Erreur',
        description: 'Les mots de passe ne correspondent pas.',
        variant: 'destructive',
      });
      return;
    }
    
    toast({
      title: 'Mot de passe modifié',
      description: 'Votre mot de passe a été mis à jour avec succès.',
    });
    
    setSecurityData({
      current_password: '',
      new_password: '',
      confirm_password: '',
      two_factor_enabled: securityData.two_factor_enabled,
    });
  };

  return (
    <>
      <Helmet>
        <title>Paramètres - LogiConnect</title>
      </Helmet>
      <DashboardLayout sidebarLinks={sidebarLinks}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">Paramètres</h1>
            <p className="text-muted-foreground mt-2">Gérez vos paramètres et préférences</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-8">
            <TabsTrigger value="profile">Profil</TabsTrigger>
            <TabsTrigger value="preferences">Préférences</TabsTrigger>
            <TabsTrigger value="security">Sécurité</TabsTrigger>
            <TabsTrigger value="notifications">Notifications</TabsTrigger>
          </TabsList>

          {/* Onglet Profil */}
          <TabsContent value="profile" className="space-y-6">
            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Building className="w-5 h-5" />
                  Informations de l'entreprise
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Identifiant LogiConnect */}
                {(user?.logiconnect_id || user?.profile_data?.logiconnect_id) && (
                  <div>
                    <Label>ID LogiConnect</Label>
                    <div className="mt-2">
                      <LogiConnectIdBadge 
                        id={user?.profile_data?.logiconnect_id || user?.logiconnect_id} 
                        showLabel={false}
                        size="default"
                      />
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="organisation_name">Nom de l'organisation</Label>
                    <Input
                      id="organisation_name"
                      value={profileData.organisation_name}
                      onChange={(e) => setProfileData(prev => ({ ...prev, organisation_name: e.target.value }))}
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_company"
                      checked={profileData.is_company}
                      onCheckedChange={(checked) => setProfileData(prev => ({ ...prev, is_company: checked }))}
                    />
                    <Label htmlFor="is_company">Entreprise</Label>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="registration_number">Numéro d'enregistrement</Label>
                    <Input
                      id="registration_number"
                      value={profileData.registration_number}
                      onChange={(e) => setProfileData(prev => ({ ...prev, registration_number: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="tax_info">Informations fiscales</Label>
                    <Input
                      id="tax_info"
                      value={profileData.tax_info}
                      onChange={(e) => setProfileData(prev => ({ ...prev, tax_info: e.target.value }))}
                    />
                  </div>
                </div>

                <div>
                  <Label>Statut de vérification</Label>
                  <div className="mt-2">
                    {getVerificationBadge(profileData.verification_status)}
                  </div>
                </div>

                <div>
                  <Label>Domaines de transport</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {profileData.transport_domains.map((domain) => (
                      <Badge key={domain} className="bg-blue-500 text-white">
                        {domain.replace('_', ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <UserCheck className="w-5 h-5" />
                  Informations personnelles
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">Prénom</Label>
                    <Input
                      id="first_name"
                      value={userData.first_name}
                      onChange={(e) => setUserData(prev => ({ ...prev, first_name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Nom</Label>
                    <Input
                      id="last_name"
                      value={userData.last_name}
                      onChange={(e) => setUserData(prev => ({ ...prev, last_name: e.target.value }))}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={userData.email}
                      onChange={(e) => setUserData(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone_number">Téléphone</Label>
                    <Input
                      id="phone_number"
                      value={userData.phone_number}
                      onChange={(e) => setUserData(prev => ({ ...prev, phone_number: e.target.value }))}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button onClick={handleSaveProfile} className="bg-primary hover:bg-primary/90">
                <Save className="w-4 h-4 mr-2" />
                Sauvegarder le profil
              </Button>
            </div>
          </TabsContent>

          {/* Onglet Préférences */}
          <TabsContent value="preferences" className="space-y-6">
            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Préférences générales
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="language">Langue</Label>
                    <Select value={preferences.language} onValueChange={(value) => setPreferences(prev => ({ ...prev, language: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fr">Français</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="timezone">Fuseau horaire</Label>
                    <Select value={preferences.timezone} onValueChange={(value) => setPreferences(prev => ({ ...prev, timezone: value }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Africa/Conakry">Conakry (GMT+0)</SelectItem>
                        <SelectItem value="Africa/Abidjan">Abidjan (GMT+0)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="auto_accept">Acceptation automatique des demandes</Label>
                      <p className="text-sm text-muted-foreground">Accepter automatiquement les demandes correspondant à vos critères</p>
                    </div>
                    <Switch
                      id="auto_accept"
                      checked={preferences.auto_accept_requests}
                      onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, auto_accept_requests: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="show_location">Afficher ma localisation</Label>
                      <p className="text-sm text-muted-foreground">Permettre aux clients de voir votre position</p>
                    </div>
                    <Switch
                      id="show_location"
                      checked={preferences.show_location}
                      onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, show_location: checked }))}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button onClick={handleSavePreferences} className="bg-primary hover:bg-primary/90">
                <Save className="w-4 h-4 mr-2" />
                Sauvegarder les préférences
              </Button>
            </div>
          </TabsContent>

          {/* Onglet Sécurité */}
          <TabsContent value="security" className="space-y-6">
            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Changer le mot de passe
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="current_password">Mot de passe actuel</Label>
                  <Input
                    id="current_password"
                    type="password"
                    value={securityData.current_password}
                    onChange={(e) => setSecurityData(prev => ({ ...prev, current_password: e.target.value }))}
                  />
                </div>
                
                <div>
                  <Label htmlFor="new_password">Nouveau mot de passe</Label>
                  <Input
                    id="new_password"
                    type="password"
                    value={securityData.new_password}
                    onChange={(e) => setSecurityData(prev => ({ ...prev, new_password: e.target.value }))}
                  />
                </div>
                
                <div>
                  <Label htmlFor="confirm_password">Confirmer le nouveau mot de passe</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={securityData.confirm_password}
                    onChange={(e) => setSecurityData(prev => ({ ...prev, confirm_password: e.target.value }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="two_factor">Authentification à deux facteurs</Label>
                    <p className="text-sm text-muted-foreground">Ajouter une couche de sécurité supplémentaire</p>
                  </div>
                  <Switch
                    id="two_factor"
                    checked={securityData.two_factor_enabled}
                    onCheckedChange={(checked) => setSecurityData(prev => ({ ...prev, two_factor_enabled: checked }))}
                  />
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button onClick={handleChangePassword} className="bg-primary hover:bg-primary/90">
                <Key className="w-4 h-4 mr-2" />
                Changer le mot de passe
              </Button>
            </div>
          </TabsContent>

          {/* Onglet Notifications */}
          <TabsContent value="notifications" className="space-y-6">
            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Préférences de notification
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="notifications_email">Notifications par email</Label>
                      <p className="text-sm text-muted-foreground">Recevoir des notifications par email</p>
                    </div>
                    <Switch
                      id="notifications_email"
                      checked={preferences.notifications_email}
                      onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, notifications_email: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="notifications_sms">Notifications par SMS</Label>
                      <p className="text-sm text-muted-foreground">Recevoir des notifications par SMS</p>
                    </div>
                    <Switch
                      id="notifications_sms"
                      checked={preferences.notifications_sms}
                      onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, notifications_sms: checked }))}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="notifications_push">Notifications push</Label>
                      <p className="text-sm text-muted-foreground">Recevoir des notifications push dans l'application</p>
                    </div>
                    <Switch
                      id="notifications_push"
                      checked={preferences.notifications_push}
                      onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, notifications_push: checked }))}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button onClick={handleSavePreferences} className="bg-primary hover:bg-primary/90">
                <Save className="w-4 h-4 mr-2" />
                Sauvegarder les notifications
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DashboardLayout>
    </>
  );
};

export default TransporterSettings;



